import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, Loader2 } from "lucide-react";
import { GATES } from "@/data/temple";
import { useJourney, currentGate, readReminder, REMINDER_KEY } from "@/lib/journey";
import { API_BASE } from "@/lib/apiBase";

const API = API_BASE;

export default function ReminderOptIn() {
  const { data } = useJourney();
  const [saved, setSaved] = useState(readReminder);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const gate = currentGate(data);
  if (!API) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/reminders/subscribe`, { email, gate });
      const rec = { email, client_token: r?.client_token ?? null };
      localStorage.setItem(REMINDER_KEY, JSON.stringify(rec));
      setSaved(rec);
      toast.success("Check your inbox and confirm with one gentle yes.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please enter a valid email address.");
    } finally {
      setBusy(false);
    }
  };

  const forget = () => {
    localStorage.removeItem(REMINDER_KEY);
    setSaved(null);
  };

  return (
    <div data-testid="reminder-optin" className="border border-[#D9B47A]/20 bg-[#0D0908] p-7 sm:p-9">
      <div className="flex items-center gap-3">
        <BellRing className="w-5 h-5 text-[#D9B47A]" />
        <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#D9B47A]">Gentle weekly reminder</p>
      </div>
      {saved ? (
        <div data-testid="reminder-saved">
          <p className="font-display text-2xl mt-5 leading-snug">Requested for <em className="text-[#D9B47A] break-all">{saved.email}</em>.</p>
          <p className="mt-3 text-sm text-[#A8998C]">Confirm from your inbox to begin. Each Sunday morning you will be called back to Gate {String(gate + 1).padStart(2, "0")} · {GATES[gate].name}, and it follows you as you progress.</p>
          <button data-testid="reminder-forget" onClick={forget} className="mt-4 text-sm text-[#A8998C] underline hover:text-[#D9B47A]">Use a different email</button>
        </div>
      ) : (
        <form onSubmit={submit}>
          <p className="font-display text-2xl mt-5 leading-snug">Once a week, a soft call back to <em className="text-[#D9B47A]">{GATES[gate].name}</em>.</p>
          <p className="mt-3 text-sm text-[#A8998C]">Sundays, 9am Sydney time. Unsubscribe in one click, any time.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <input data-testid="reminder-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className="flex-1 bg-transparent border-b border-[#D9B47A]/30 py-3 text-lg placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] transition-colors" />
            <button data-testid="reminder-submit" disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C2414F] px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#F2E8DF] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Remind me
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
